import { useNavigate } from 'react-router';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { managementReportingService } from '@/core/services/managementReporting';
import type { DimensionRow, DimensionValueRow, ManagementOption } from '@/core/models/management-reporting.models';
import { validateForm } from '@/features/manage/formRules';
import {
  confirmDelete,
  guardClient,
  MatButton,
  RequiredWrap,
  useLiveState,
  useOnInit,
  useSelectedClient,
} from './shared';
import './Dimensions.scss';

interface DimensionForm {
  id: string | null;
  code: string;
  name: string;
  kind: number;
  isActive: boolean;
  forThisClient: boolean;
}

interface ValueForm {
  id: string | null;
  code: string;
  name: string;
  isActive: boolean;
}

interface State {
  dataSource: DimensionRow[];
  kinds: ManagementOption[];
  selected: DimensionRow | null;
  form: DimensionForm;
  valueForm: ValueForm;
  showForm: boolean;
  /** A failed Save marks the empty required boxes red. */
  formTried: boolean;
  valueTried: boolean;
  /** In-flight flags: Save cannot be sent twice. */
  saving: boolean;
  savingValue: boolean;
}

const emptyForm = (): DimensionForm => ({ id: null, code: '', name: '', kind: 0, isActive: true, forThisClient: true });
const emptyValueForm = (): ValueForm => ({ id: null, code: '', name: '', isActive: true });

/** Dimensions and cost centres used by management reporting (Angular TaxreturnDimensionsComponent). */
export function TaxreturnDimensions() {
  const navigate = useNavigate();
  const client = useSelectedClient();
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    kinds: [],
    selected: null,
    form: emptyForm(),
    valueForm: emptyValueForm(),
    showForm: false,
    formTried: false,
    valueTried: false,
    saving: false,
    savingValue: false,
  });

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }
    managementReportingService.getOptions().then(
      (options) => set({ kinds: options.dimensionKinds }),
      (error) => notifyError(error),
    );
    load();
  });

  function load(): void {
    if (!client) {
      return;
    }
    managementReportingService.getDimensions(client.id).then(apply, (error) => notifyError(error));
  }

  /** The member panel stays open across saves: `selected` is re-resolved by id from the new rows. */
  function apply(rows: DimensionRow[]): void {
    set((current) => ({
      dataSource: rows,
      selected: current.selected ? (rows.find((r) => r.id === current.selected!.id) ?? null) : null,
    }));
  }

  const setForm = (patch: Partial<DimensionForm>) => set((current) => ({ form: { ...current.form, ...patch } }));
  const setValueForm = (patch: Partial<ValueForm>) =>
    set((current) => ({ valueForm: { ...current.valueForm, ...patch } }));

  const onAdd = () => set({ form: emptyForm(), showForm: true, formTried: false });

  const onEdit = (row: DimensionRow) =>
    set({
      form: {
        id: row.id,
        code: row.code,
        name: row.name,
        kind: row.kind,
        isActive: row.isActive,
        forThisClient: !!row.clientId,
      },
      showForm: true,
      formTried: false,
    });

  const onCancel = () => set({ showForm: false, form: emptyForm(), formTried: false });

  function onSave(): void {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }

    const form = live.current.form;
    if (!form.code.trim() || !form.name.trim()) {
      set({ formTried: true });
      validateForm(false);
      return;
    }

    set({ saving: true });
    managementReportingService
      .saveDimension({
        id: form.id,
        clientId: form.forThisClient ? client.id : null,
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        isActive: form.isActive,
      })
      .then(
        (rows) => {
          apply(rows);
          onCancel();
          set({ saving: false });
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => {
          set({ saving: false });
          notifyError(error);
        },
      );
  }

  async function onDelete(row: DimensionRow): Promise<void> {
    if (!(await confirmDelete('Dimension'))) {
      return;
    }
    managementReportingService.deleteDimensions([row.id]).then(
      () => {
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
        set({ selected: null });
        load();
      },
      (error) => notifyError(error),
    );
  }

  const onSelect = (row: DimensionRow) => set({ selected: row, valueForm: emptyValueForm(), valueTried: false });

  const onEditValue = (value: DimensionValueRow) =>
    set({
      valueForm: { id: value.id, code: value.code, name: value.name, isActive: value.isActive },
      valueTried: false,
    });

  function onSaveValue(): void {
    const { selected, valueForm } = live.current;
    if (!selected) {
      notifyError('Select a dimension first.');
      return;
    }
    if (!valueForm.code.trim() || !valueForm.name.trim()) {
      set({ valueTried: true });
      validateForm(false);
      return;
    }

    set({ savingValue: true });
    managementReportingService
      .saveDimensionValue({
        id: valueForm.id,
        dimensionId: selected.id,
        code: valueForm.code.trim(),
        name: valueForm.name.trim(),
        isActive: valueForm.isActive,
      })
      .then(
        (rows) => {
          apply(rows);
          set({ valueForm: emptyValueForm(), valueTried: false, savingValue: false });
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => {
          set({ savingValue: false });
          notifyError(error);
        },
      );
  }

  function onDeleteValue(value: DimensionValueRow): void {
    managementReportingService.deleteDimensionValues([value.id]).then(
      () => {
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
        load();
      },
      (error) => notifyError(error),
    );
  }

  /** Reads the dimensions the client's imported trial balance lines already carry. */
  function onCapture(): void {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    managementReportingService.captureDimensions(client.id).then(
      (result) => {
        apply(result.dimensions);
        notifySuccess(
          result.captured === 0
            ? 'No new dimensions were found on the imported trial balance lines.'
            : `${result.captured} dimension member(s) captured.`,
        );
      },
      (error) => notifyError(error),
    );
  }

  const columns: DataTableColumn<DimensionRow>[] = [
    { id: 'code', header: 'Code', headerClassName: 'w-150', cell: (row) => row.code },
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'kind', header: 'Kind', headerClassName: 'w-180', cell: (row) => row.kindName },
    { id: 'scope', header: 'Scope', headerClassName: 'w-150', cell: (row) => (row.clientId ? 'Client' : 'Firm') },
    { id: 'members', header: 'Members', headerClassName: 'w-100', cell: (row) => row.values.length },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-200',
      cell: (row) => (
        <>
          <Tooltip title="Members">
            <MatButton icon="list" iconOutlined onClick={() => onSelect(row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <MatButton icon="edit" iconOutlined onClick={() => onEdit(row)} />
          </Tooltip>
          <Tooltip title="Delete">
            <MatButton icon="delete_outline" iconOutlined onClick={() => onDelete(row)} />
          </Tooltip>
        </>
      ),
    },
  ];

  const valueColumns: DataTableColumn<DimensionValueRow>[] = [
    { id: 'code', header: 'Code', headerClassName: 'w-180', cell: (row) => row.code },
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'active', header: 'Active', headerClassName: 'w-100', cell: (row) => (row.isActive ? 'Yes' : 'No') },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-150',
      cell: (row) => (
        <>
          <Tooltip title="Edit">
            <MatButton icon="edit" iconOutlined onClick={() => onEditValue(row)} />
          </Tooltip>
          <Tooltip title="Delete">
            <MatButton icon="delete_outline" iconOutlined onClick={() => onDeleteValue(row)} />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-dimensions">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <MatButton className="action-button primary-button" icon="add" onClick={onAdd}>
              <span>Dimension</span>
            </MatButton>
            <MatButton className="action-button secondary-button" icon="cloud_download" onClick={onCapture}>
              <span>Capture from imports</span>
            </MatButton>
          </div>
        </div>

        {s.showForm && (
          <div className="content-header pt-1 mb-0 hr-top">
            <div className="d-flex gap-20 align-items-center flex-wrap">
              <RequiredWrap>
                <InputField
                  fieldClassName="w-180"
                  maxLength={50}
                  placeholder="Code"
                  charMode="alphanumeric"
                  invalid={s.formTried && !s.form.code.trim()}
                  value={s.form.code}
                  onChange={(event) => setForm({ code: event.target.value })}
                />
              </RequiredWrap>
              <RequiredWrap>
                <InputField
                  fieldClassName="w-270"
                  maxLength={255}
                  placeholder="Name"
                  charMode="text"
                  invalid={s.formTried && !s.form.name.trim()}
                  value={s.form.name}
                  onChange={(event) => setForm({ name: event.target.value })}
                />
              </RequiredWrap>
              <Select
                className="w-180"
                items={s.kinds}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                searchable={false}
                value={s.form.kind}
                onChange={(value) => setForm({ kind: value as number })}
              />
              <Checkbox
                checked={s.form.forThisClient}
                onChange={(event) => setForm({ forThisClient: event.target.checked })}
              >
                This client only
              </Checkbox>
              <Checkbox checked={s.form.isActive} onChange={(event) => setForm({ isActive: event.target.checked })}>
                Active
              </Checkbox>
              <Button className="action-button primary-button" busy={s.saving} onClick={onSave}>
                Save
              </Button>
              <Button className="action-button secondary-button" onClick={onCancel}>
                Cancel
              </Button>
            </div>
            {s.formTried && (!s.form.code.trim() || !s.form.name.trim()) && (
              <p className="text-danger mb-0 mt-1">Enter the dimension code and name.</p>
            )}
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(row) => row.id} />
        </div>

        {s.selected && (
          <div className="hr-top pt-1 mt-1">
            <div className="text-gray font-size-18 fw-normal mb-1">
              Members of <span className="fw-bold">{s.selected.name}</span>
            </div>
            <div className="d-flex gap-20 align-items-center flex-wrap mb-1">
              <RequiredWrap>
                <InputField
                  fieldClassName="w-180"
                  maxLength={100}
                  placeholder="Code"
                  charMode="alphanumeric"
                  invalid={s.valueTried && !s.valueForm.code.trim()}
                  value={s.valueForm.code}
                  onChange={(event) => setValueForm({ code: event.target.value })}
                />
              </RequiredWrap>
              <RequiredWrap>
                <InputField
                  fieldClassName="w-270"
                  maxLength={255}
                  placeholder="Name"
                  charMode="text"
                  invalid={s.valueTried && !s.valueForm.name.trim()}
                  value={s.valueForm.name}
                  onChange={(event) => setValueForm({ name: event.target.value })}
                />
              </RequiredWrap>
              <Checkbox
                checked={s.valueForm.isActive}
                onChange={(event) => setValueForm({ isActive: event.target.checked })}
              >
                Active
              </Checkbox>
              <Button className="action-button primary-button" busy={s.savingValue} onClick={onSaveValue}>
                Save member
              </Button>
            </div>
            {s.valueTried && (!s.valueForm.code.trim() || !s.valueForm.name.trim()) && (
              <p className="text-danger mb-1">Enter the code and name of the dimension member.</p>
            )}
            <div className="mat-table-wrapper-import">
              <DataTable stickyHeader columns={valueColumns} rows={s.selected.values} rowKey={(row) => row.id} />
            </div>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
